import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

import rocodromo1 from '@/assets/projects/rocodromo-1.webp';
import rocodromo5 from '@/assets/projects/rocodromo-5.webp';
import rocodromo11 from '@/assets/projects/rocodromo-11.webp';
import boulderGym from '@/assets/projects/boulder-gym.webp';
import parqueAventura from '@/assets/projects/parque-aventura-aereo.webp';
import rocodromo19 from '@/assets/projects/rocodromo-19.webp';

const FeaturedProjectsSection: React.FC = () => {
  const { t } = useLanguage();

  const projects = [
    {
      image: rocodromo1,
      title: t('home.featured.1.title'),
      location: t('home.featured.1.location'),
      alt: 'Rocódromo indoor profesional escalada España',
    },
    {
      image: boulderGym,
      title: t('home.featured.2.title'),
      location: t('home.featured.2.location'),
      alt: 'Boulder gym muro de escalada indoor',
    },
    {
      image: rocodromo5,
      title: t('home.featured.3.title'),
      location: t('home.featured.3.location'),
      alt: 'Construcción rocódromo competición escalada',
    },
    {
      image: parqueAventura,
      title: t('home.featured.4.title'),
      location: t('home.featured.4.location'),
      alt: 'Parque de aventura aéreo inspección certificación',
    },
    {
      image: rocodromo11,
      title: t('home.featured.5.title'),
      location: t('home.featured.5.location'),
      alt: 'Centro de escalada indoor boulder rocódromo',
    },
    {
      image: rocodromo19,
      title: t('home.featured.6.title'),
      location: t('home.featured.6.location'),
      alt: 'Muro de escalada diseño moderno rocódromo',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('home.featured.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('home.featured.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
              whileHover={{ y: -4 }}
            >
              <img
                src={project.image}
                alt={project.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-5 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-primary-foreground font-semibold text-lg leading-tight">
                  {project.title}
                </h3>
                <p className="text-primary-foreground/70 text-sm mt-1">
                  {project.location}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
        >
          <Button asChild size="lg">
            <Link to="/proyectos">
              {t('home.featured.cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProjectsSection;
